import {
    TimeSeriesItem,
    TimeSeriesPoint,
} from "~/modules/token_timeseries/types";

// // // //

/**
 * Interpolates missing dates in a time series by filling gaps with the previous day's value.
 * Ensures there is a single point for each day from the first to the last date.
 */
export function interpolateMissingDates(item: TimeSeriesItem): TimeSeriesItem {
    if (item.points.length === 0) {
        return item;
    }

    // Sort points by date to ensure chronological order
    const sortedPoints = [...item.points].sort((a, b) => {
        const dateA = new Date(a[0]).getTime();
        const dateB = new Date(b[0]).getTime();
        return dateA - dateB;
    });

    const firstDate = new Date(sortedPoints[0][0]);
    const lastDate = new Date(sortedPoints[sortedPoints.length - 1][0]);

    // Create a map of existing points for quick lookup using normalized date strings
    // Normalize dates to YYYY-MM-DD format for consistent comparison
    const normalizeDateString = (dateStr: string): string => {
        const date = new Date(dateStr);
        return date.toISOString().split("T")[0];
    };

    const pointsMap = new Map(
        sortedPoints.map((point) => [normalizeDateString(point[0]), point[1]]),
    );

    const interpolatedPoints: TimeSeriesPoint[] = [];
    let previousValue = sortedPoints[0][1]; // Start with the first value

    // Iterate through each day from first to last date
    const currentDate = new Date(firstDate);
    while (currentDate <= lastDate) {
        const normalizedDateStr = currentDate.toISOString().split("T")[0];

        if (pointsMap.has(normalizedDateStr)) {
            // Use the actual value if it exists
            const actualValue = pointsMap.get(normalizedDateStr)!;
            interpolatedPoints.push([normalizedDateStr, actualValue]);
            previousValue = actualValue; // Update previous value
        } else {
            // Use the previous day's value for missing dates
            interpolatedPoints.push([normalizedDateStr, previousValue]);
        }

        // Move to the next day
        currentDate.setDate(currentDate.getDate() + 1);
    }

    return {
        ...item,
        points: interpolatedPoints,
    };
}
